import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { RefreshCw, ExternalLink } from "lucide-react";

const SEV_COLOR = { critical: "#EF4444", high: "#F59E0B", medium: "#F5C542", low: "#8892A6" };
const SEV_LABEL = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };
const TYPE_LABEL = {
  task_due_soon: "Task due soon", task_overdue: "Task overdue",
  task_overdue_high: "High-priority overdue task", project_health: "Project health",
  project_dormant: "Project dormant", milestone_upcoming: "Milestone approaching",
  decision_aging: "Aging decision", payment_due_soon: "Payment due soon",
  payment_overdue: "Payment overdue", budget_threshold: "Budget threshold",
  recurring_upcoming: "Recurring transaction",
};

function deepLinkFor(a) {
  if (!a.entity_id) return null;
  const map = { task: "tasks", project: "projects", note: "notes",
                expenditure: "budget", recurring: "budget", event: "calendar" };
  const tab = map[a.source_type] || null;
  return tab ? `/entity/${a.entity_id}?tab=${tab}&focus=${a.source_id}` : `/entity/${a.entity_id}`;
}

export default function AttentionPage() {
  const [tab, setTab] = useState("active");
  const [alerts, setAlerts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, s] = await Promise.all([
      api.get(`/attention/alerts?status=${tab}&limit=200`),
      api.get("/me/attention_settings"),
    ]);
    setAlerts(a.data || []);
    setSettings(s.data || null);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/attention/evaluate", {});
      toast.success(`Active: ${r.data.active_count} · New: ${r.data.new_or_escalated} · Resolved: ${r.data.resolved}`);
      await load();
    } catch (e) { toast.error("Refresh failed"); }
    finally { setBusy(false); }
  };

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      const r = await api.put("/me/attention_settings", patch);
      setSettings(r.data);
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <div className="h-full overflow-y-auto p-8" data-testid="attention-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-eyebrow mb-2">HQ Attention</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Alerts</h1>
          <p className="text-sm text-white/50 mt-1">Proactive signals from the same data driving Right Now, Focus Strip, and Project Health.</p>
        </div>
        <Button onClick={refresh} disabled={busy} className="rounded-full bg-white text-black hover:bg-white/90" data-testid="attention-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} /> {busy ? "Evaluating…" : "Refresh"}
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-6">
        {["active", "resolved"].map((k) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full text-sm ${tab === k ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
            data-testid={`attention-tab-${k}`}>
            {k[0].toUpperCase() + k.slice(1)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-2" data-testid="attention-list">
          {alerts.length === 0 && (
            <div className="text-white/50 text-sm p-6 rounded-lg border border-white/10 bg-[#151A23]">
              {tab === "active" ? "Nothing pressing." : "No resolved alerts yet."}
            </div>
          )}
          {alerts.map((a) => {
            const link = deepLinkFor(a);
            return (
              <div key={a.id} className="p-4 rounded-lg border border-white/10 bg-[#151A23]" data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: SEV_COLOR[a.severity] || SEV_COLOR.low }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ background: `${SEV_COLOR[a.severity]}22`, color: SEV_COLOR[a.severity] }}>
                        {SEV_LABEL[a.severity] || a.severity}
                      </span>
                      <span className="text-[11px] text-white/40 uppercase tracking-wider">{TYPE_LABEL[a.alert_type] || a.alert_type}</span>
                    </div>
                    <div className="text-sm font-medium text-white/90 truncate">{a.title}</div>
                    <div className="text-xs text-white/60 mt-0.5">{a.message}</div>
                    <div className="text-[11px] text-white/40 mt-1">
                      First detected {a.first_detected_at?.slice(0, 10)} · Last seen {a.last_detected_at?.slice(0, 10)}
                      {a.resolved_at && ` · Resolved ${a.resolved_at.slice(0,10)}`}
                    </div>
                  </div>
                  {link && (
                    <Link to={link} className="text-white/50 hover:text-white p-1.5 rounded hover:bg-white/10" title="Open source" data-testid={`alert-open-${a.id}`}>
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {settings && (
          <aside className="p-4 rounded-xl border border-white/10 bg-[#151A23] space-y-4" data-testid="attention-settings">
            <div className="text-eyebrow">Settings</div>
            <SwitchRow label="Proactive alerts enabled" testId="setting-enabled"
              checked={!!settings.enabled} onChange={(v) => saveSettings({ enabled: v })} />
            <SwitchRow label="Immediate alerts" testId="setting-immediate"
              checked={!!settings.immediate_enabled} onChange={(v) => saveSettings({ immediate_enabled: v })} />
            <SwitchRow label="Daily attention email" testId="setting-daily"
              checked={!!settings.daily_enabled} onChange={(v) => saveSettings({ daily_enabled: v })} />
            <NumField label="Daily send hour (0-23, local time)" testId="setting-hour"
              value={settings.daily_send_hour} min={0} max={23}
              onChange={(v) => saveSettings({ daily_send_hour: v })} />
            <NumField label="Dormant project threshold (days)" testId="setting-dormant"
              value={settings.dormant_days} min={1} max={90}
              onChange={(v) => saveSettings({ dormant_days: v })} />
            <NumField label="Due-soon window (days)" testId="setting-duesoon"
              value={settings.due_soon_days} min={1} max={30}
              onChange={(v) => saveSettings({ due_soon_days: v })} />
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Timezone</Label>
              <Input value={settings.timezone || "UTC"}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                onBlur={(e) => saveSettings({ timezone: e.target.value.trim() || "UTC" })}
                className="mt-1.5 bg-white/5 border-white/10" data-testid="setting-timezone" />
            </div>
            <div className="text-[11px] text-white/40 leading-relaxed">
              Emails are sent from Alias HQ via Resend and respect your project + financial permissions. Nothing goes out when there is nothing to report.
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function SwitchRow({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-white/80">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

function NumField({ label, value, min, max, onChange, testId }) {
  return (
    <div>
      <Label className="text-white/60 text-xs uppercase tracking-wider">{label}</Label>
      <Input type="number" min={min} max={max} value={value ?? ""}
        onChange={(e) => onChange(Math.max(min, Math.min(max, parseInt(e.target.value || "0", 10))))}
        className="mt-1.5 bg-white/5 border-white/10 w-24" data-testid={testId} />
    </div>
  );
}
